import { Box, Flex, Menu, Text, Stack } from '@mantine/core'
import { IconBell } from '@tabler/icons'
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react'
import CommentsSection from '../CommentsSection'
import { BusinessNameNotification, FillIconNotification } from '../../../../../icons/x-symbol-svgrepo-com'
import IdeaContext from '../../../IdeaContext'
import SingleCustomerContext from '../../../../Customer360/Customer-Context/SingleCustomerContext'
import DiscoveryContext from '../../../../Discovery/DiscoveryContext'
import { useIdea } from '../../../../../hooks/useIdea'

const CommentsMenu = ({
  componentType = "idea",
  chosenEvidence: propChosenEvidence,
  getUnreadNotificationsCountByRequest: propGetUnreadNotificationsCountByRequest
}: {
  componentType?: string;
  chosenEvidence?: any;
  getUnreadNotificationsCountByRequest?: (requestId: string) => number;
}) => {

  const isIdeaPage: boolean = componentType === 'idea'
  const isDiscoveryPage: boolean = componentType === 'discovery'
  const isCustomerPage: boolean = componentType === 'customer'
  const isAiPage: boolean = componentType === 'ai'
  
  const parseQueryParams = (search: string) => {
    return new URLSearchParams(search);
  };
  const queryParams = parseQueryParams(location.search);
  const commentId = queryParams.get("commentId");
  
  // Get ideaId from URL for AI component
  const ideaId = isAiPage ? queryParams.get("ideaId") || undefined : undefined;
  
  // Use useIdea hook for AI component
  const aiIdeaData = useIdea();

  const ContextToUse: React.Context<any> = 
  isIdeaPage ? IdeaContext : isDiscoveryPage ? DiscoveryContext : SingleCustomerContext;

  const contextData = useContext(ContextToUse);
  const { 
    isCommentPresent: contextIsCommentPresent, 
    comments: contextComments, 
    setComments: contextSetComments, 
    getUnreadNotificationsCountByRequest: contextGetUnreadNotificationsCountByRequest,
    chosenEvidence: contextChosenEvidence, 
    activityOpened: contextActivityOpened, 
    setActivityOpened: contextSetActivityOpened 
  } = contextData || {};

  // Use AI data if componentType is 'ai', otherwise use context
  const isCommentPresent = useMemo(() =>
    isAiPage ? aiIdeaData.isCommentPresent : contextIsCommentPresent,
  [isAiPage, aiIdeaData.isCommentPresent, contextIsCommentPresent]
  );

  const comments = useMemo(() =>
    isAiPage ? aiIdeaData.comments : contextComments,
  [isAiPage, aiIdeaData.comments, contextComments]
  );

  const setComments = useCallback(
    (value: any) => {
      if (isAiPage && aiIdeaData.setIsCommentPresent) {
        aiIdeaData.setIsCommentPresent(value);
      } else if (contextSetComments && typeof contextSetComments === 'function') {
        contextSetComments(value);
      }
    },
    [isAiPage, aiIdeaData.setIsCommentPresent, contextSetComments]
  );

  const getUnreadNotificationsCountByRequest = useCallback(
    (requestId: string) => {
      if (isAiPage && aiIdeaData.getUnreadNotificationsCountByRequest) {
        return aiIdeaData.getUnreadNotificationsCountByRequest(requestId);
      } else if (contextGetUnreadNotificationsCountByRequest && typeof contextGetUnreadNotificationsCountByRequest === 'function') {
        return contextGetUnreadNotificationsCountByRequest(requestId);
      }
      return 0;
    },
    [isAiPage, aiIdeaData.getUnreadNotificationsCountByRequest, contextGetUnreadNotificationsCountByRequest]
  );

  const chosenEvidence = useMemo(() =>
    isAiPage ? propChosenEvidence : contextChosenEvidence,
  [isAiPage, propChosenEvidence, contextChosenEvidence]
  );

  const activityOpened = useMemo(() =>
    isAiPage ? aiIdeaData.activityOpened : contextActivityOpened,
  [isAiPage, aiIdeaData.activityOpened, contextActivityOpened]
  );

  const setActivityOpened = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      if (isAiPage && aiIdeaData.setActivityOpened) {
        aiIdeaData.setActivityOpened(value);
      } else if (contextSetActivityOpened && typeof contextSetActivityOpened === 'function') {
        contextSetActivityOpened(value);
      }
    },
    [isAiPage, aiIdeaData.setActivityOpened, contextSetActivityOpened]
  );

  /* console.log({item}) */

  useEffect(() => {
    if (setComments && typeof setComments === 'function' && isCommentPresent !== undefined) {
      setComments(isCommentPresent || []);
    }
  }, [isCommentPresent]); // Remove setComments from dependencies

  const commentExist = comments?.length > 0;
  
  // Use ref to track if commentId has been processed to prevent loop
  const commentIdProcessedRef = React.useRef(false);
  
  useEffect(() => {
    if (commentId && setActivityOpened && typeof setActivityOpened === 'function' && !commentIdProcessedRef.current) {
      setActivityOpened(true);
      commentIdProcessedRef.current = true;
    }
  }, [commentId]); // Remove setActivityOpened from dependencies
  
  return (
   
    <Menu opened={activityOpened || false} 
      position="left" 
      offset={isCustomerPage ? 52 : 35}
      closeOnItemClick={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      styles={{
        dropdown: {
          position: 'fixed',
          zIndex: 1000,
          top: isCustomerPage ? '200px' : '10px',
          /* height: '99%', */
          overflow: 'hidden'
        }
      }}>
      <Menu.Target>
        <Flex align="center" w="107px" mr="12px" sx={{ cursor: "pointer" }}>
          <Box>
            {getUnreadNotificationsCountByRequest?.(chosenEvidence?._id) > 0 ? <BusinessNameNotification/> :  commentExist ? <FillIconNotification/> : <IconBell size={15} color="#5C5CEB" />}
          </Box>

          <Text ml="4px" fw={500} color="#5C5CEB" fz={12} lineClamp={1} onClick={()=>setActivityOpened && typeof setActivityOpened === 'function' ? setActivityOpened((prev: boolean)=> !prev) : null}>
                Activity
          </Text>
        </Flex>
      </Menu.Target>

      <Menu.Dropdown p={0} m={0}>
        <Box>
          <CommentsSection 
            item={chosenEvidence} 
            setOpened={setActivityOpened && typeof setActivityOpened === 'function' ? setActivityOpened : () => null} 
            componentType={componentType} 
            commentId={commentId}/>
        </Box>
      </Menu.Dropdown>
    </Menu>
   
  )
}

export default CommentsMenu